<template>
  <Teleport to="body">
    <div v-if="props.userId" class="modal-overlay">
      <div class="detail-container" @click.stop>
        <div class="modal-header">
          <h1 class="detail-title">User Detail</h1>
          <button class="close-btn" @click="handleClose">
            <img :src="closeFillIcon" alt="close" class="close-icon" />
          </button>
        </div>

        <LoadingIndicator v-if="loading && !user" min-height="240px" />

        <div v-else-if="loadError && !user" class="detail-error">
          <p class="detail-error-text">{{ loadError }}</p>
          <button type="button" class="detail-error-close" @click="handleClose">Close</button>
        </div>

        <template v-else-if="user">
          <div class="modal-body">
            <div class="info-row">
              <span class="info-label">User ID:</span>
              <span class="info-value">{{ displayUserId }}</span>
            </div>

            <div class="info-row">
              <span class="info-label">Registered Mobile:</span>
              <span class="info-value" :class="{ 'info-value--empty': !registeredMobileLinked }">
                {{ registeredMobileDisplay }}
              </span>
            </div>

            <div class="info-row">
              <span class="info-label">Google Account:</span>
              <span class="info-value" :class="{ 'info-value--empty': !googleAccountLinked }">
                {{ googleAccountDisplay }}
              </span>
            </div>

            <div class="info-row">
              <span class="info-label">Apple Account:</span>
              <span class="info-value" :class="{ 'info-value--empty': !appleAccountLinked }">
                {{ appleAccountDisplay }}
              </span>
            </div>

            <div class="info-row">
              <span class="info-label">Status:</span>
              <span
                class="info-value status-text"
                :class="{ inactive: user.status !== USER_LIST_STATUS_ACTIVE }"
              >
                {{ statusText }}
              </span>
            </div>

            <template v-for="(home, homeIndex) in detailHomes" :key="`${home.home_id}-${homeIndex}`">
              <div class="section-divider"></div>

              <div class="info-row info-row--address">
                <span class="info-label">Home {{ homeIndex + 1 }}:</span>
                <span class="info-value">{{ home.address || 'N/A' }}</span>
              </div>

              <div class="info-row">
                <span class="info-label">Home ID:</span>
                <span class="info-value">{{ home.home_id || 'N/A' }}</span>
              </div>

              <div class="info-row">
                <span class="info-label">Type:</span>
                <span class="info-value">{{ homeType(home) }}</span>
              </div>

              <div class="info-row">
                <span class="info-label">Year Built:</span>
                <span class="info-value">{{ displayField(home.year_built) }}</span>
              </div>

              <div class="info-row">
                <span class="info-label">Bedrooms:</span>
                <span class="info-value">{{ displayField(home.bedrooms) }}</span>
              </div>

              <div class="info-row">
                <span class="info-label">Bathrooms:</span>
                <span class="info-value">{{ displayField(home.bathrooms) }}</span>
              </div>

              <div class="info-row">
                <span class="info-label">Size:</span>
                <span class="info-value">{{ home.size_label || home.size || 'N/A' }}</span>
              </div>

              <div class="info-row">
                <span class="info-label">Subscribed Plan:</span>
                <span
                  class="info-value"
                  :class="{
                    'plan-link': homePlan(home).subscribed,
                    'plan-unsubscribed': !homePlan(home).subscribed,
                  }"
                  @click="openSubscribedPlan(home)"
                >
                  {{ homePlan(home).text }}
                </span>
              </div>

              <div class="info-row">
                <span class="info-label">HM Care Orders:</span>
                <a
                  v-if="isOrderCountClickable(home, 'hmc_orders')"
                  href="#"
                  class="order-count"
                  @click.prevent="openLinkedOrders(home)"
                >{{ orderCountDisplay(home, 'hmc_orders') }}</a>
                <span v-else class="order-count order-count--plain">{{ orderCountDisplay(home, 'hmc_orders') }}</span>
              </div>

              <div class="info-row">
                <span class="info-label">Follow-up Orders:</span>
                <a
                  v-if="isOrderCountClickable(home, 'fu_orders')"
                  href="#"
                  class="order-count"
                  @click.prevent="openLinkedOrders(home)"
                >{{ orderCountDisplay(home, 'fu_orders') }}</a>
                <span v-else class="order-count order-count--plain">{{ orderCountDisplay(home, 'fu_orders') }}</span>
              </div>

              <div class="info-row">
                <span class="info-label">AD Orders:</span>
                <span class="order-count order-count--plain">{{ orderCountDisplay(home, 'ad_orders') }}</span>
              </div>
            </template>
          </div>

          <div class="detail-footer">
            <div class="section-divider"></div>
            <div class="detail-actions">
              <button
                v-if="user.status === USER_LIST_STATUS_ACTIVE"
                class="action-btn action-btn--deactivate"
                @click="handleDeactivate"
              >
                <span class="action-icon-wrap" aria-hidden="true">
                  <img :src="deactivateIcon" alt="" class="action-icon-img action-icon-default" />
                  <img :src="deactivateWhiteIcon" alt="" class="action-icon-img action-icon-hover" />
                </span>
                <span class="action-btn-label">Deactivate</span>
              </button>
              <button v-else class="action-btn action-btn--reactivate" @click="handleReactivate">
                <span class="action-icon-wrap" aria-hidden="true">
                  <img :src="activateIcon" alt="" class="action-icon-img action-icon-default" />
                  <img :src="activateWhiteIcon" alt="" class="action-icon-img action-icon-hover" />
                </span>
                <span class="action-btn-label">Reactivate</span>
              </button>
              <button class="action-btn action-btn--update-mobile" @click="handleUpdateMobile">
                <span class="action-icon-wrap" aria-hidden="true">
                  <img :src="mobileIcon" alt="" class="action-icon-img action-icon-default" />
                  <img :src="mobileWhiteIcon" alt="" class="action-icon-img action-icon-hover" />
                </span>
                <span class="action-btn-label">Update Mobile</span>
              </button>
            </div>
          </div>
        </template>
      </div>

      <UserMobileStatus
        v-if="showMobileStatusModal && user"
        :user-id="props.userId"
        :mode="mobileStatusMode"
        :phone="registeredMobile"
        :phone-code="user.phone_code"
        @close="closeMobileStatusModal"
        @saved="handleMobileStatusSaved"
      />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject } from 'vue'
import { useRouter } from 'vue-router'
import { getUserDetail, type UserDetail as UserDetailData, type UserLinkedAccount } from '@/api/userdetail'
import { USER_LIST_STATUS_ACTIVE, type UserHomeItem } from '@/api/userslist'
import { writeHmCareOrdersListPrefill } from '@/api/CareOrderDetailFocus'
import { writeCareSubscriptionListPrefill } from '@/api/careSubscription'
import { careOrdersListRouteForSub } from '@/utils/careOrdersRoutes'
import {
  buildUserHomeOrdersListPrefill,
  resolvePlanLabel,
  resolveUserDisplayId,
  resolveUserHomes,
  resolveUserOrderLines,
} from '@/utils/userListDisplay'
import closeFillIcon from '@/assets/Fill 1@2x.png'
import deactivateIcon from '@/assets/Schedule.png'
import activateIcon from '@/assets/activate.png'
import mobileIcon from '@/assets/mobile.png'
import deactivateWhiteIcon from '@/assets/deactivate_white@2x (1).png'
import activateWhiteIcon from '@/assets/activate_white@2x.png'
import mobileWhiteIcon from '@/assets/mobile_white@2x.png'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import UserMobileStatus from './UserMobileStatus.vue'

const showMobileStatusModal = ref(false)
const mobileStatusMode = ref<'update' | 'deactivate' | 'reactivate'>('update')

const emit = defineEmits<{
  close: []
  updated: [userId: string]
}>()

const props = defineProps<{
  userId: string
}>()

const router = useRouter()
const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>('showToast')

const user = ref<UserDetailData | null>(null)
const loading = ref(false)
const loadError = ref('')
let fetchGeneration = 0

function resolveDetailRequestId(raw: string): string | number {
  const trimmed = raw.trim()
  if (/^\d+$/.test(trimmed)) return Number(trimmed)
  return trimmed
}

function isDetailSuccessCode(code: unknown): boolean {
  return code === 200 || code === '200'
}

const displayUserId = computed(() => {
  if (!user.value) return ''
  return resolveUserDisplayId(user.value)
})

const registeredMobile = computed(() => {
  if (!user.value) return ''
  return (
    user.value.registered_mobile?.trim() ||
    user.value.mobile?.trim() ||
    user.value.phone?.trim() ||
    ''
  )
})

const registeredMobileLinked = computed(() => Boolean(registeredMobile.value))
const registeredMobileDisplay = computed(() => registeredMobile.value || 'Not link')

function isLinkedAccountObject(value: unknown): value is UserLinkedAccount {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function resolveLinkedAccount(account: string | UserLinkedAccount | undefined, ...fallbacks: Array<string | undefined>): {
  linked: boolean
  display: string
} {
  if (isLinkedAccountObject(account)) {
    const display =
      account.display?.trim() ||
      account.email?.trim() ||
      fallbacks.map((v) => v?.trim()).find(Boolean) ||
      ''
    const linked =
      account.linked === true ||
      account.linked === 1 ||
      Boolean(account.email?.trim())
    return {
      linked: linked || Boolean(display && !/^not\s*link$/i.test(display)),
      display: display || 'Not Link',
    }
  }

  const fromString = typeof account === 'string' ? account.trim() : ''
  const fallback = fallbacks.map((v) => v?.trim()).find(Boolean) || ''
  const display = fromString || fallback
  return {
    linked: Boolean(display),
    display: display || 'Not Link',
  }
}

const googleAccount = computed(() => {
  if (!user.value) return { linked: false, display: 'Not Link' }
  return resolveLinkedAccount(user.value.google_account, user.value.google_email, user.value.email)
})

const appleAccount = computed(() => {
  if (!user.value) return { linked: false, display: 'Not Link' }
  return resolveLinkedAccount(user.value.apple_account, user.value.apple_email, user.value.apple_id)
})

const googleAccountLinked = computed(() => googleAccount.value.linked)
const appleAccountLinked = computed(() => appleAccount.value.linked)

const googleAccountDisplay = computed(() => googleAccount.value.display)
const appleAccountDisplay = computed(() => appleAccount.value.display)

const statusText = computed(() => {
  if (!user.value) return ''
  if (user.value.status === USER_LIST_STATUS_ACTIVE) return 'Active'
  return user.value.status_text?.trim() || 'Inactive'
})

const detailHomes = computed((): UserHomeItem[] => {
  if (!user.value) return []
  return resolveUserHomes(user.value)
})

function displayField(value: string | number | undefined): string {
  if (value == null || value === '') return 'N/A'
  return String(value)
}

function homeType(home: UserHomeItem): string {
  return home.type?.trim() || home.home_type?.trim() || 'N/A'
}

function homePlan(home: UserHomeItem) {
  return resolvePlanLabel(home)
}

function openSubscribedPlan(home: UserHomeItem): void {
  if (!homePlan(home).subscribed) return
  const homeId = String(home.home_id ?? '').trim()
  if (homeId && homeId.toUpperCase() !== 'N/A') {
    writeCareSubscriptionListPrefill({ searchType: 'home_id', keyword: homeId })
  } else {
    const userId = displayUserId.value.trim()
    if (!userId) {
      showToast?.('warning', 'User not found.')
      return
    }
    writeCareSubscriptionListPrefill({ searchType: 'user_id', keyword: userId })
  }
  void router.push(careOrdersListRouteForSub('hm_care_subscription'))
}

function normalizeOrderStatKey(key: string | undefined): string {
  const k = (key ?? '').trim().toLowerCase()
  if (k === 'hmc' || k === 'hmc_orders') return 'hmc_orders'
  if (k === 'fu' || k === 'fu_orders') return 'fu_orders'
  if (k === 'ad' || k === 'ad_orders') return 'ad_orders'
  return k
}

function orderCountValue(home: UserHomeItem, key: 'hmc_orders' | 'fu_orders' | 'ad_orders'): number {
  const lines = resolveUserOrderLines(home)
  const hit = lines.find((line) => normalizeOrderStatKey(line.statKey) === key)
  if (hit?.count != null) return hit.count

  const direct = home[key]
  if (typeof direct === 'number') return direct

  const stats = home.order_stats
  if (stats && !Array.isArray(stats)) {
    const value = stats[key]
    if (typeof value === 'number') return value
  }

  if (hit?.display) {
    const parsed = Number(hit.display.replace(/[^\d.-]/g, ''))
    if (Number.isFinite(parsed)) return parsed
  }

  return 0
}

function orderCountDisplay(home: UserHomeItem, key: 'hmc_orders' | 'fu_orders' | 'ad_orders'): string {
  const lines = resolveUserOrderLines(home)
  const hit = lines.find((line) => normalizeOrderStatKey(line.statKey) === key)
  if (hit?.display?.trim()) return hit.display.trim()

  const stats = home.order_stats
  if (stats && !Array.isArray(stats)) {
    const displayKey = `${key}_display` as const
    const fromStats = stats[displayKey]
    if (typeof fromStats === 'string' && fromStats.trim()) return fromStats.trim()
  }

  return String(orderCountValue(home, key))
}

function isOrderCountClickable(
  home: UserHomeItem,
  key: 'hmc_orders' | 'fu_orders',
): boolean {
  return orderCountValue(home, key) > 0
}

function openLinkedOrders(home: UserHomeItem): void {
  const prefill = buildUserHomeOrdersListPrefill(home.home_id ?? '', displayUserId.value)
  if (!prefill) return
  writeHmCareOrdersListPrefill(prefill)
  void router.push(careOrdersListRouteForSub('hm_orders'))
}

async function fetchDetail() {
  if (!props.userId) {
    emit('close')
    return
  }

  const generation = ++fetchGeneration
  loading.value = true
  loadError.value = ''
  try {
    const response = await getUserDetail(resolveDetailRequestId(props.userId))
    if (generation !== fetchGeneration) return
    if (isDetailSuccessCode(response.code) && response.data && typeof response.data === 'object') {
      user.value = response.data
      loadError.value = ''
      return
    }
    console.error('Failed to fetch user detail:', response.msg || response.code)
    const message = response.msg || 'Failed to load user detail'
    loadError.value = message
    showToast?.('error', message)
  } catch (err) {
    if (generation !== fetchGeneration) return
    console.error('Failed to fetch user detail:', err)
    loadError.value = 'Failed to load user detail'
    showToast?.('error', 'Failed to load user detail')
  } finally {
    if (generation === fetchGeneration) {
      loading.value = false
    }
  }
}

function handleClose(): void {
  emit('close')
}

function handleDeactivate(): void {
  mobileStatusMode.value = 'deactivate'
  showMobileStatusModal.value = true
}

function handleReactivate(): void {
  mobileStatusMode.value = 'reactivate'
  showMobileStatusModal.value = true
}

function closeMobileStatusModal(): void {
  showMobileStatusModal.value = false
}

function handleMobileStatusSaved(): void {
  showMobileStatusModal.value = false
  void fetchDetail()
  emit('updated', props.userId)
}

function handleUpdateMobile(): void {
  mobileStatusMode.value = 'update'
  showMobileStatusModal.value = true
}

watch(
  () => props.userId,
  (newId) => {
    user.value = null
    loadError.value = ''
    if (newId) void fetchDetail()
  },
  { immediate: true },
)
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 24px 24px 45px 24px;
  z-index: 10000;
  box-sizing: border-box;
}

.detail-container {
  background-color: #ffffff;
  border-radius: 12px;
  width: 540px;
  max-width: calc(100% - 48px);
  height: calc(100vh - 69px);
  max-height: calc(100vh - 69px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.detail-error {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  box-sizing: border-box;
}

.detail-error-text {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: #666666;
  text-align: center;
}

.detail-error-close {
  min-width: 80px;
  height: 32px;
  padding: 0 16px;
  border: 1px solid #d0d0d0;
  border-radius: 16px;
  background: #ffffff;
  color: #000000;
  font-size: 14px;
  cursor: pointer;
}

.modal-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 24px;
  flex-shrink: 0;
}

.detail-title {
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  font-family:
    'SF Pro',
    SFPro,
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  /* 顶 24 + 行高 20 + body padding-top 16 = 标题到首行 16px */
  margin: 24px 0 0;
  text-align: left;
}

.close-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  margin-top: 24px;
}

.modal-body {
  /* 冒号竖线距弹窗左 ≈ 137px（540 - 403）；User ID 左缘 ≈ 85px */
  --ud-colon-x: 137px;
  --ud-label-width: 130px;
  --ud-pad-left: calc(var(--ud-colon-x) - var(--ud-label-width));
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* 标题到首行 16px */
  padding: 16px 24px 0 var(--ud-pad-left);
  display: flex;
  flex-direction: column;
  gap: 16px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.modal-body::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 20px;
}

.info-row--address {
  align-items: center;
}

.info-label {
  box-sizing: border-box;
  flex: 0 0 var(--ud-label-width);
  width: var(--ud-label-width);
  text-align: right;
  white-space: nowrap;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  font-style: normal;
}

.info-value {
  flex: 1;
  min-width: 0;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  word-break: break-word;
}

.info-value--empty {
  color: #cccccc;
}

.status-text {
  color: #00aeef;
  font-weight: 600;
}

.status-text.inactive {
  color: #fb0101;
}

.plan-link {
  text-decoration: underline;
  color: #000000;
  cursor: pointer;
}

.plan-unsubscribed {
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #cccccc;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  text-decoration: none;
}

.order-count {
  color: #000000;
  text-decoration: underline;
  font-size: 14px;
  line-height: 20px;
  padding: 0;
  background-color: transparent;
  transition: none;
}

.order-count--plain {
  text-decoration: none;
  cursor: default;
}

.order-count:hover,
.order-count:focus,
.order-count:active {
  color: #000000;
  background-color: transparent;
  text-decoration: underline;
}

.order-count--plain:hover,
.order-count--plain:focus,
.order-count--plain:active {
  text-decoration: none;
}

.section-divider {
  width: 100%;
  height: 1px;
  background: #e0e0e0;
  flex-shrink: 0;
}

.modal-body .section-divider {
  /* 与 footer / 右侧 24px 对齐（body 左侧为冒号校准的较小 padding） */
  margin-left: calc(24px - var(--ud-pad-left));
  width: calc(100% - (24px - var(--ud-pad-left)));
}

.detail-footer {
  flex-shrink: 0;
  /* 内容/标题到分割线 16px；底部分割线到按钮见下方 margin-bottom */
  padding: 16px 24px 24px;
}

.detail-footer .section-divider {
  margin-bottom: 16px;
}

.detail-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
}

.action-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid #000000;
  border-radius: 16px;
  background: #ffffff;
  color: #000000;
  cursor: pointer;
}

.action-btn--deactivate {
  width: 109px;
}

.action-btn--update-mobile {
  width: 135px;
}

.action-btn--reactivate {
  height: 28px;
  border-radius: 16px;
}

.action-btn-label {
  box-sizing: border-box;
  height: 20px;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  font-style: normal;
  white-space: nowrap;
}

.action-btn--deactivate .action-btn-label {
  width: 69px;
  text-align: center;
}

.action-btn--update-mobile .action-btn-label {
  width: 95px;
  text-align: left;
}

.action-btn--reactivate .action-btn-label {
  text-align: center;
}

.action-btn:hover {
  background: #000000;
  color: #ffffff;
}

.action-btn:hover .action-btn-label {
  color: #ffffff;
}

.action-icon-wrap {
  position: relative;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.action-icon-img {
  position: absolute;
  inset: 0;
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.action-icon-hover {
  opacity: 0;
}

.action-btn:hover .action-icon-default {
  opacity: 0;
}

.action-btn:hover .action-icon-hover {
  opacity: 1;
}
</style>
